import {
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  forwardRef,
  type FormEvent,
} from 'react';
import { Spinner } from './Spinner';

interface ReplyBarProps {
  onSend: (body: string) => void;
  /** Disabled while a previous send is mid-flight. */
  busy?: boolean;
  placeholder?: string;
  /** Optional left-side button (e.g. Templates picker). */
  leftSlot?: React.ReactNode;
}

export interface ReplyBarHandle {
  /** Replace the current draft with the given text. */
  setText: (next: string) => void;
  /** Move keyboard focus into the input. Used after Take Over so the
   *  owner can start typing immediately. */
  focus: () => void;
}

const sendIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

// Max height the textarea grows to before it stops growing and starts
// scrolling internally. ~6 lines at our 14px font + 1.5 line-height + the
// vertical padding inside the pill. iMessage feels about right at this size.
const MAX_TEXTAREA_HEIGHT_PX = 140;

/**
 * iMessage-style reply bar — auto-growing textarea + circular send button.
 *
 * Layout: `position: fixed bottom: 0` always (iOS pins fixed-bottom-0
 * elements to the visualViewport bottom, so the bar floats above the
 * keyboard when up and at the screen bottom when down). A same-height
 * placeholder lives in the flex layout above so the message scroll
 * container reserves space; the placeholder is sized by ResizeObserver so
 * as the textarea grows the messages list stays scrolled correctly above
 * it.
 *
 * Wrap behavior: typing past the input width wraps to a new line and the
 * bar grows taller (capped at MAX_TEXTAREA_HEIGHT_PX). Like iMessage, the
 * iOS keyboard's Return key inserts a newline — sending is via the blue
 * arrow button. Ernie 2026-05-23.
 */
export const ReplyBar = forwardRef<ReplyBarHandle, ReplyBarProps>(function ReplyBar(
  { onSend, busy = false, placeholder = 'Send a message…', leftSlot },
  ref,
) {
  const [text, setText] = useState('');
  const [focused, setFocused] = useState(false);
  const [barHeight, setBarHeight] = useState(56);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);

  // Quick-reply pickers + other surfaces poke text into the bar via ref.
  useImperativeHandle(
    ref,
    () => ({
      setText: (next: string) => setText(next),
      focus: () => inputRef.current?.focus(),
    }),
    [],
  );

  // Auto-grow the textarea: reset to natural height, measure scrollHeight,
  // cap. Runs on every text change so programmatic setText (from quick
  // replies) resizes the same way typed input does.
  useLayoutEffect(() => {
    const ta = inputRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, MAX_TEXTAREA_HEIGHT_PX)}px`;
  }, [text]);

  // Track the bar's real height into the placeholder so messages list stays
  // scrolled correctly above the bar as the textarea grows. ResizeObserver
  // catches BOTH textarea growth (user typing) AND padding changes
  // (focus/blur safe-area toggle).
  useEffect(() => {
    const form = formRef.current;
    if (!form) return;
    setBarHeight(form.offsetHeight);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const h = entry.contentRect.height;
        // contentRect excludes padding — add it back via offsetHeight
        // since we care about the bar's visual footprint, not just content.
        setBarHeight(form.offsetHeight || h);
      }
    });
    ro.observe(form);
    return () => ro.disconnect();
  }, []);

  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    const body = text.trim();
    if (!body || busy) return;
    onSend(body);
    setText('');
  };

  const canSend = text.trim().length > 0 && !busy;

  return (
    <>
      <div aria-hidden style={{ height: barHeight, flexShrink: 0 }} />
      <form
        ref={formRef}
        onSubmit={submit}
        className="flex items-end gap-2 border-t border-ink-200 dark:border-[#334155] bg-white/95 dark:bg-[#0F172A]/95 backdrop-blur-md px-3 pt-2 transition-[padding] duration-150 ease-ss-standard"
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 50,
          paddingBottom: focused
            ? '8px'
            : 'calc(8px + env(safe-area-inset-bottom, 0px))',
        }}
      >
        {leftSlot}
        <textarea
          ref={inputRef}
          rows={1}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder={placeholder}
          aria-label="Reply"
          // resize:none — disable the native textarea resize handle; we
          // grow programmatically via the useLayoutEffect above.
          // overflow:auto kicks in only after we hit MAX_TEXTAREA_HEIGHT_PX
          // (the textarea has overflow-y-auto inside its style box).
          // leading-snug = 1.375 line-height — iMessage-tight.
          className="flex-1 appearance-none resize-none rounded-3xl border-0 bg-ink-100 dark:bg-[#1E293B] px-3.5 py-2 text-sm leading-snug text-ink-900 dark:text-[#E2E8F0] placeholder:text-ink-400 dark:placeholder:text-[#64748B] focus:outline-none focus:ring-2 focus:ring-ss-blue/30 overflow-y-auto"
          style={{ maxHeight: MAX_TEXTAREA_HEIGHT_PX }}
        />
        <button
          type="submit"
          disabled={!canSend}
          aria-label={busy ? 'Sending reply' : 'Send reply'}
          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-[color:var(--ss-accent-fg,white)] transition-opacity disabled:opacity-40"
          style={{ background: 'var(--ss-accent, #2563EB)' }}
        >
          {busy ? <Spinner size={16} /> : sendIcon}
        </button>
      </form>
    </>
  );
});
